import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { fadeInOut } from '@commons/animations/fade-in-out';
import { loaderIndicator } from '@commons/animations/loader';
import { slideDown } from '@commons/animations/slide';
import {
  ContextAccountComponent,
  ContextJobsHomeComponent,
  ContextJobsOfferComponent,
  ContextMoneyHomeComponent,
} from '@commons/context';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { FeaturesRoutingEnum } from '@features/feature-routing-enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RouterState } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { ServicesState } from '@stores/services/services.state';
import { SessionState } from '@stores/session/session.state';
import { CountryService } from '@wizbii/utils/models';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

const SignUpContext: Record<string, Type<any>> = {
  'wizbii-account': ContextAccountComponent,
  account: ContextAccountComponent,
  'wizbii-jobs-home': ContextJobsHomeComponent,
  jobs: ContextJobsHomeComponent,
  'fibii-home': ContextMoneyHomeComponent,
  fibii: ContextMoneyHomeComponent,
  'wizbii-jobs-offer': ContextJobsOfferComponent,
  default: ContextAccountComponent,
};

@UntilDestroy()
@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  animations: [fadeInOut(), loaderIndicator(), slideDown()],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SignUpComponent implements OnInit, OnDestroy {
  readonly #breakpointsService = inject(BreakpointsService);
  readonly #store = inject(Store);
  readonly #route = inject(ActivatedRoute);

  @ViewChild('contextComponent', { static: true, read: ViewContainerRef }) contextRef: ViewContainerRef;

  @Select(ServicesState.services)
  servicesList$: Observable<CountryService[]>;

  @Select(SessionState.currentContext)
  currentContext$: Observable<string>;

  @Select(SessionState.currentAppId)
  currentAppId$: Observable<string>;

  @Select(SessionState.contextCoupon)
  couponStr$: Observable<string>;

  isCouponValid$ = new BehaviorSubject<boolean>(false);
  sponsoringCode$ = new BehaviorSubject<string>(undefined);
  extraContent$ = new BehaviorSubject<string>(undefined);

  FeaturesRoutingEnum = FeaturesRoutingEnum;

  showSignupEmail$ = new BehaviorSubject<boolean>(false);

  get isMobile$(): Observable<boolean> {
    return this.#breakpointsService.isMobile$;
  }

  get isMobileAndTabletPortrait$(): Observable<boolean> {
    return this.#breakpointsService.isMobileAndTabletPortrait$;
  }

  constructor() {
    combineLatest([this.couponStr$.pipe(distinctUntilChanged()), this.currentContext$.pipe(distinctUntilChanged())])
      .pipe(
        untilDestroyed(this),
        switchMap(() => of(null))
      )
      .subscribe((coupon) => (coupon ? this.isCouponValid$.next(true) : this.isCouponValid$.next(false)));
  }

  // tslint:disable-next-line: cognitive-complexity
  ngOnInit(): void {
    this.initContext();

    this.#store
      .select(RouterState.state)
      .pipe(
        map((state: any) => state.data),
        map((data) => data.signupMethod),
        untilDestroyed(this)
      )
      .subscribe((signupMethod) => {
        this.showSignupEmail$.next(!signupMethod || signupMethod === 'email');
      });

    this.#route.queryParamMap.pipe(untilDestroyed(this)).subscribe((queryParamMap) => {
      this.sponsoringCode$.next(
        queryParamMap.has('redirect') && queryParamMap.get('redirect').includes('sponsoringCode')
          ? queryParamMap.get('redirect').split('sponsoringCode=')[1]
          : undefined
      );
    });
  }

  // tslint:disable-next-line: cognitive-complexity
  private initContext(): void {
    combineLatest([
      combineLatest([
        this.currentContext$.pipe(distinctUntilChanged()),
        this.currentAppId$.pipe(distinctUntilChanged()),
        this.isCouponValid$.pipe(distinctUntilChanged()),
        this.sponsoringCode$.pipe(distinctUntilChanged()),
      ]).pipe(
        distinctUntilChanged(),
        map(([context, appId]) => {
          return [context, appId];
        }),
        map(([context, appId]) =>
          context && context !== 'default' ? SignUpContext[context] : appId ? SignUpContext[appId] : null
        ),
        map((component) => (component ? component : SignUpContext['default'])),
        untilDestroyed(this)
      ),
      this.#breakpointsService.isMobileAndTabletPortrait$,
    ]).subscribe(([component, isMobileAndTabletPortrait]) => {
      if (!isMobileAndTabletPortrait) {
        const contextRef = this.contextRef;

        if (this.contextRef) {
          this.contextRef.clear();
        }

        const ref = contextRef.createComponent<any>(component);
        ref.changeDetectorRef.detectChanges();
      } else {
        this.contextRef.clear();
      }
    });
  }

  trackByService(_: number, service: CountryService): string {
    return service.id;
  }

  ngOnDestroy(): void {
    this.showSignupEmail$.complete();
    this.isCouponValid$.complete();
  }
}
